import {
    Component,
    HostListener,
    Input,
    output,
    ChangeDetectionStrategy,
    OnDestroy,
    OnInit,
    inject,
} from '@angular/core';
import { FiLikesService } from '@fi-app/shared/fi-likes/services/fi-likes.service';
import { IReactionModel } from '@fi-shared/fi-likes/fi-likes';
import { Subject } from 'rxjs';
import { ActionType, ObjectTypes } from '@fi-shared/types';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { getClientGuid } from '@fi-shared/utils/client-guid';
import { FiLikesFactoryService } from '@fi-app/shared/fi-likes/services/fi-likes-factory.service';

@Component({
    selector: 'fi-like-button',
    template: `<ng-content></ng-content>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FiLikeButtonComponent implements OnInit, OnDestroy {
    @Input() objectId: number;
    @Input() objectType: ObjectTypes;
    @Input() likes: IReactionModel[];

    public onLikesChange = output<IReactionModel[]>();

    private likesService = inject(FiLikesService);
    private fiSignalRService = inject(FiSignalRService);
    private fiLikesFactoryService = inject(FiLikesFactoryService);

    ngOnInit(): void {
        this.fiSignalRService.reactionsUpdatedEvent.subscribe((data) => {
            if (
                data.ObjectId === this.objectId &&
                data.ObjectType === this.objectType &&
                data.ClientGuid !== getClientGuid()
            ) {
                data.Reactions.forEach((like) => {
                    const currentLikeIdx = this.likes.findIndex((x) => x.UserId === like.UserId);
                    if (data.ActionType === ActionType.Create && currentLikeIdx === -1) {
                        this.likes.push(like);
                    } else if (data.ActionType === ActionType.Delete && currentLikeIdx !== -1) {
                        this.likes.splice(currentLikeIdx, 1);
                    }
                    this.onLikesChange.emit(this.likes);
                });
            }
        }, this.ngOnDestroy$);
    }

    private getCurrentUsersLike(): IReactionModel {
        return this.likesService.getCurrentUsersLike(this.likes);
    }
    private isLikeInProcess = false;

    @HostListener('click', ['$event'])
    toggleLike($event) {
        $event.stopPropagation();
        if (this.isLikeInProcess) {
            return;
        }
        this.isLikeInProcess = true;

        const currentUsersLike = this.getCurrentUsersLike();
        if (currentUsersLike) {
            // delete
            this.likesService
                .deleteLike(this.fiLikesFactoryService.getLikeInstanceForSave(currentUsersLike))
                .then(() => {
                    this.likes.splice(this.likes.indexOf(currentUsersLike), 1);
                    this.onLikesChange.emit(this.likes);
                })
                .finally(() => (this.isLikeInProcess = false));
        } else {
            const newLike = this.fiLikesFactoryService.getNewCommonLikeInstance(this.objectId, this.objectType);
            this.likesService
                .saveLike(this.fiLikesFactoryService.getLikeInstanceForSave(newLike))
                .then(() => {
                    this.likes.push(newLike);
                    this.onLikesChange.emit(this.likes);
                })
                .finally(() => (this.isLikeInProcess = false));
        }
    }
    canToggleLike() {
        return !this.isLikeInProcess;
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
